/**
 * Video Board BroadcastChannel — cross-tab sync for program output.
 * Main sim tab broadcasts; video board tab receives.
 *
 * Video items are sent as data attributes (data-video-src) since <canvas>
 * mirrors don't serialize. The video board hydrates them into <video> elements.
 *
 * Continuous time sync: the main sim broadcasts a "video-sync" heartbeat
 * every 500ms with each shared video's currentTime. The video board corrects
 * drift so both tabs stay frame-accurate (like ReplaySIM ↔ SwitcherSIM sync).
 */

import { getSharedVideoTime, getSharedVideoUrls } from './templateRenderer';

type VideoTime = { src: string; currentTime: number };

type MessageData =
  | { type: 'program-update'; html: string; videoTimes: VideoTime[] }
  | { type: 'program-clear' }
  | { type: 'video-sync'; videoTimes: VideoTime[] }
  | { type: 'focus-sim' }
  | { type: 'lesson-complete'; lessonName: string };

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel {
  if (!channel) {
    channel = new BroadcastChannel('nevco-videoboard');
  }
  return channel;
}

/* ── Sender API (main sim tab) ── */

let syncInterval: ReturnType<typeof setInterval> | null = null;

/**
 * Start the continuous video time sync heartbeat.
 * Broadcasts currentTime for all shared videos every 500ms.
 */
export function startVideoSync(): void {
  if (syncInterval) return;
  syncInterval = setInterval(() => {
    const urls = getSharedVideoUrls();
    if (urls.length === 0) return;
    const videoTimes: VideoTime[] = urls.map((src) => ({
      src,
      currentTime: getSharedVideoTime(src),
    }));
    getChannel().postMessage({ type: 'video-sync', videoTimes } as MessageData);
  }, 500);
}

/**
 * Stop the continuous video time sync heartbeat.
 */
export function stopVideoSync(): void {
  if (syncInterval) {
    clearInterval(syncInterval);
    syncInterval = null;
  }
}

/**
 * Broadcast program state. Replaces <canvas> elements (video mirrors) with
 * serializable <div data-video-src="..."> placeholders before sending.
 * Includes currentTime for each video so the receiver can seek.
 */
export function broadcastProgramState(sourceEl: HTMLElement): void {
  // Clone the DOM so we can mutate it for serialization
  const clone = sourceEl.cloneNode(true) as HTMLElement;

  const videoTimes: VideoTime[] = [];

  // Replace canvas video mirrors with serializable placeholders carrying the URL
  clone.querySelectorAll('canvas[data-video-src]').forEach((canvas) => {
    const src = canvas.getAttribute('data-video-src')!;
    const placeholder = document.createElement('div');
    placeholder.setAttribute('data-video-src', src);
    placeholder.style.cssText = canvas.style.cssText || 'width:100%;height:100%;background:#000;';
    canvas.replaceWith(placeholder);

    videoTimes.push({ src, currentTime: getSharedVideoTime(src) });
  });

  getChannel().postMessage({ type: 'program-update', html: clone.innerHTML, videoTimes } as MessageData);

  // Store video URLs in localStorage so the video board can preload on open
  if (videoTimes.length > 0) {
    try { localStorage.setItem('vb-video-urls', JSON.stringify(videoTimes)); } catch (_) {}
  }

  // Ensure sync heartbeat is running when we have program content
  startVideoSync();
}

export function broadcastProgramClear(): void {
  getChannel().postMessage({ type: 'program-clear' } as MessageData);
  stopVideoSync();
}

export function broadcastLessonComplete(lessonName: string): void {
  getChannel().postMessage({ type: 'lesson-complete', lessonName } as MessageData);
  stopVideoSync();
}

/**
 * Send program-clear when the sim tab closes or refreshes,
 * so the video board resets to idle.
 */
export function initUnloadBroadcast(): void {
  window.addEventListener('beforeunload', () => {
    getChannel().postMessage({ type: 'program-clear' } as MessageData);
  });
}

/** Send a focus request from the video board to the sim tab. */
export function sendFocusSim(): void {
  getChannel().postMessage({ type: 'focus-sim' } as MessageData);
}

/** Listen for focus requests (called in main sim tab). */
export function onFocusSim(callback: () => void): void {
  getChannel().addEventListener('message', (e: MessageEvent<MessageData>) => {
    if (e.data.type === 'focus-sim') {
      callback();
    }
  });
}

/* ── Receiver API (video board tab) ── */

export function onProgramUpdate(callback: (html: string, videoTimes: VideoTime[]) => void): void {
  getChannel().addEventListener('message', (e: MessageEvent<MessageData>) => {
    if (e.data.type === 'program-update') {
      callback(e.data.html, e.data.videoTimes);
    }
  });
}

export function onProgramClear(callback: () => void): void {
  getChannel().addEventListener('message', (e: MessageEvent<MessageData>) => {
    if (e.data.type === 'program-clear') {
      callback();
    }
  });
}

/**
 * Listen for continuous video time sync heartbeats.
 * Callback receives the latest timestamps to correct local video drift.
 */
export function onVideoSync(callback: (videoTimes: VideoTime[]) => void): void {
  getChannel().addEventListener('message', (e: MessageEvent<MessageData>) => {
    if (e.data.type === 'video-sync') {
      callback(e.data.videoTimes);
    }
  });
}

export function onLessonComplete(callback: (lessonName: string) => void): void {
  getChannel().addEventListener('message', (e: MessageEvent<MessageData>) => {
    if (e.data.type === 'lesson-complete') {
      callback(e.data.lessonName);
    }
  });
}
